import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button, Card, Col, Container, Form, FormControl, InputGroup, Row, Table } from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import { Option } from 'react-bootstrap-typeahead/types/types';
import { useNavigate } from 'react-router-dom';

import { createReturnEmpties, getEmptiesByCustomerOrder } from '../../../apis/queries';
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal';
import EmptyTableState from '../../../components/EmptyState/EmptyStateTable';
import Loader from '../../../components/Loader';
import { GET_EMPTIES_CUSTOMERS_KEY, GET_EMPTIES_KEY } from '../Empties';

interface CustomerOption {
  _id: string;
  name: string;
}

interface Product {
  productId: string;
  name: string;
  quantity: number;
}

export interface Empty {
  orderId: string;
  orderNumber: string;
  products: Product[];
}

interface ReturnEmptiesProps {
  customers: CustomerOption[] | undefined;
  initialLoading: boolean;
}

type FormControlElement = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

const ReturnEmpties = ({ customers, initialLoading }: ReturnEmptiesProps) => {
  const [selectedQuantities, setSelectedQuantities] = useState<{ [orderId: string]: { [productId: string]: number } }>(
    {}
  );
  const [paymentModes, setPaymentModes] = useState('');
  const [isSubmitVisible, setIsSubmitVisible] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerOption | null>(null);

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: emptiesData, isLoading: isLoadingEmpties } = useQuery({
    queryKey: ['customer', 'empties', { id: selectedCustomer?._id }],
    queryFn: () => (selectedCustomer ? getEmptiesByCustomerOrder(selectedCustomer._id) : null),
    enabled: !!selectedCustomer?._id
  });

  const handleQuantityChange = (orderId: string, productId: string, quantity: number) => {
    setSelectedQuantities(prev => ({
      ...prev,
      [orderId]: {
        ...prev[orderId],
        [productId]: quantity
      }
    }));

    const anySelected =
      emptiesData?.some(() =>
        Object.values({ ...selectedQuantities[orderId], [productId]: quantity }).some(qty => qty > 0)
      ) ?? false;
    setIsSubmitVisible(anySelected);
  };

  const handlePaymentModeChange = (e: React.ChangeEvent<FormControlElement>) => {
    if (e.target instanceof HTMLSelectElement) {
      setPaymentModes(e.target.value);
    }
  };

  const incrementQuantity = (orderId: string, productId: string, maxQuantity: number) => {
    const currentQuantity = selectedQuantities[orderId]?.[productId] || 0;
    if (currentQuantity < maxQuantity) {
      handleQuantityChange(orderId, productId, currentQuantity + 1);
    }
  };

  const decrementQuantity = (orderId: string, productId: string) => {
    const currentQuantity = selectedQuantities[orderId]?.[productId] || 0;
    if (currentQuantity > 0) {
      handleQuantityChange(orderId, productId, currentQuantity - 1);
    }
  };

  const resetSelections = () => {
    setSelectedQuantities({});
    setPaymentModes('');
    setIsSubmitVisible(false);
  };

  const handleCustomerSelection = (selected: Option[]) => {
    if (selected.length === 0) {
      setSelectedCustomer(null);
      resetSelections();
    } else {
      setSelectedCustomer(selected[0] as CustomerOption);
      resetSelections();
    }
  };

  const mutation = useMutation({
    mutationFn: (data: {
      customerId: string;
      amount: string;
      paymentMode: string;
      type: string;
      emptiesItems: { productId: string; quantity: string }[];
    }) => createReturnEmpties(data.customerId, data.amount, data.paymentMode, data.type, data.emptiesItems),
    onSuccess: () => {
      setShowConfirmation(true);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [GET_EMPTIES_KEY, GET_EMPTIES_CUSTOMERS_KEY]
      });
    }
  });

  const handleCreateReturnEmpties = () => {
    if (!selectedCustomer) return;

    const emptiesItems = Object.entries(selectedQuantities).flatMap(([orderId, products]) =>
      Object.entries(products).map(([productId, quantity]) => ({ orderId, productId, quantity: quantity.toString() }))
    );

    if (emptiesItems.length === 0) return;

    if (!paymentModes) return;

    const payload = {
      amount: '0',
      paymentMode: paymentModes,
      customerId: selectedCustomer._id,
      type: 'returned',
      emptiesItems
    };

    mutation.mutate(payload);
  };

  const handleCloseConfirmation = () => {
    setLoading(true);

    setTimeout(() => {
      setLoading(false);
      setShowConfirmation(false);
      navigate('/empties/customers');
    }, 2000);
  };

  const mutationLoading = mutation?.isPending;

  return (
    <div className="main-content">
      <Container fluid>
        <Row className="justify-content-center">
          <Col xs={12} lg={10} xl={10} className="py-6">
            <Row className="justify-content-center">
              <Col xs={12} md={10} lg={8} xl={6} className="text-center">
                <h1 className="my-4">Return Empties</h1>
                <p className="mb-5 text-muted">Search for the customer by name</p>
              </Col>
            </Row>
            <div className="form-group">
              <Form.Label>Customer name</Form.Label>
              <Typeahead
                id="customer-typeahead"
                labelKey="name"
                onChange={handleCustomerSelection}
                options={customers ?? []}
                placeholder="Select customer..."
                multiple={false}
              />
            </div>

            {selectedCustomer && (
              <div className="my-6">
                {isLoadingEmpties || initialLoading ? (
                  <div className="d-flex justify-content-center align-items-center">
                    <Loader />
                  </div>
                ) : emptiesData?.length === 0 ? (
                  <EmptyTableState message="No empties available for this customer." />
                ) : (
                  <>
                    {emptiesData?.map(empty => (
                      <div key={empty.orderId}>
                        <Card>
                          <Card.Header>
                            <h4 className="card-header-title">{empty.orderNumber}</h4>
                          </Card.Header>
                          <Table size="sm" className="card-table table-nowrap" responsive>
                            <thead>
                              <tr>
                                <th>Product</th>
                                <th>Available Stock</th>
                                <th>Quantity Owed</th>
                              </tr>
                            </thead>
                            <tbody className="fs-base">
                              {empty.products.map(product => (
                                <tr key={product.productId}>
                                  <td>{product.name}</td>
                                  <td>{product.quantity}</td>
                                  <td>
                                    <InputGroup size="sm">
                                      <Button
                                        variant="outline-secondary"
                                        onClick={() => decrementQuantity(empty.orderId, product.productId)}
                                        disabled={(selectedQuantities[empty.orderId]?.[product.productId] || 0) <= 0}
                                      >
                                        -
                                      </Button>
                                      <FormControl
                                        size="sm"
                                        type="number"
                                        value={selectedQuantities[empty.orderId]?.[product.productId] || ''}
                                        onChange={e =>
                                          handleQuantityChange(
                                            empty.orderId,
                                            product.productId,
                                            parseInt(e.target.value) || 0
                                          )
                                        }
                                        min="0"
                                        max={product.quantity}
                                      />
                                      <Button
                                        variant="outline-secondary"
                                        onClick={() =>
                                          incrementQuantity(empty.orderId, product.productId, product.quantity)
                                        }
                                        disabled={
                                          (selectedQuantities[empty.orderId]?.[product.productId] || 0) >=
                                          product.quantity
                                        }
                                      >
                                        +
                                      </Button>
                                    </InputGroup>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </Card>
                      </div>
                    ))}
                    {isSubmitVisible && (
                      <div className="mt-6">
                        <Form.Group>
                          <Form.Label>Payment Mode</Form.Label>
                          <Form.Control
                            as="select"
                            value={paymentModes || ''}
                            onChange={e => handlePaymentModeChange(e)}
                          >
                            <option value="">Select payment mode...</option>
                            <option value="cash">Cash</option>
                            <option value="bottles">Bottles</option>
                          </Form.Control>
                        </Form.Group>
                      </div>
                    )}
                    {isSubmitVisible && paymentModes && (
                      <Button
                        className="mt-6"
                        variant="primary"
                        onClick={handleCreateReturnEmpties}
                        disabled={mutationLoading}
                      >
                        {mutationLoading ? 'Submitting...' : 'Submit'}
                      </Button>
                    )}
                  </>
                )}
              </div>
            )}

            <ConfirmationModal
              show={showConfirmation}
              onHide={handleCloseConfirmation}
              title="Return Empties Confirmation"
              bodyContent={
                <div>
                  <img src="/img/kanban/confirmation.jpg" alt="confirmation" style={{ width: '150px' }} />
                  <p>Empties has been returned successfully!</p>
                </div>
              }
              footerButtonText="Back to Empties"
              loading={loading}
            />
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ReturnEmpties;
